/* Embed-only display options (theme, autorun) ride in the query string so the
 * embed entry can read them without decoding the payload. */

import type { EmbedTheme } from './share-payload';

/** Browsers cap URLs (~2k is the safe ceiling across the web stack). Past this
 * we warn and point at the deferred server short-link as the escape hatch. */
export const URL_WARN_LEN = 2000;
/** Very long (~40KB+ of code): chat/social truncation + no QR become
 * near-certain. Stronger warning tier; for code this large a server short-link
 * is the real fix, not heavier compression. */
export const URL_SEVERE_LEN = 4000;
/** qrcode-generator byte capacity tops out ~2.3k chars; beyond it, skip QR. */
export const QR_MAX_LEN = 2200;

export interface EmbedOptions {
  theme: EmbedTheme;
  run: boolean;
}

export function buildShareUrl(token: string, origin: string): string {
  return `${origin}/#${token}`;
}

export type ShareNetwork = 'x' | 'fb' | 'rd';

/** Lives outside the lazy Share modal so app-shell quick-share buttons can
 * reuse it without pulling the modal chunk into the shell bundle graph. */
export function socialShareIntent(network: ShareNetwork, url: string): string {
  const u = encodeURIComponent(url);
  const text = encodeURIComponent('PHP snippet on php.onl');
  switch (network) {
    case 'x':
      return `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    case 'fb':
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'rd':
      return `https://www.reddit.com/submit?url=${u}&title=${text}`;
  }
}

export function buildEmbedUrl(token: string, opts: EmbedOptions, origin: string): string {
  const q = new URLSearchParams();
  q.set('theme', opts.theme);
  if (opts.run) q.set('run', '1');
  return `${origin}/embed?${q.toString()}#${token}`;
}

export function buildEmbedSnippet(token: string, opts: EmbedOptions, origin: string): string {
  const src = buildEmbedUrl(token, opts, origin);
  return (
    `<iframe src="${src}"\n` +
    `        width="100%" height="420" frameborder="0"\n` +
    `        style="border-radius:12px;border:1px solid #eee"\n` +
    `        title="php.onl snippet"></iframe>`
  );
}

export function parseEmbedOptions(search: string): EmbedOptions {
  const q = new URLSearchParams(search);
  return {
    theme: q.get('theme') === 'dark' ? 'dark' : 'light',
    run: q.get('run') === '1',
  };
}
